import { v4 as uuidv4 } from 'uuid';

import { FormRecord, Validators } from '@angular/forms';

import { QuestionStateService } from '@core/services';

import { FieldEqualsPipe } from '@shared/pipes';

import { PersonType } from '@/tax/models/person-type.enum';

import { generateActivations, matchGroups } from '../utils';
import { FormUtil } from './form-util.model';
import { QuestionField } from './question-field.model';
import { QuestionType } from './question-type.enum';
import { NestedQuestion } from './question.model';
import { TaxResponseGroup } from './tax-response-group.model';

export class FormUtilAccountant extends FormUtil {
    declarantForm?: FormRecord;
    declarantGroup?: TaxResponseGroup;
    partnerForm?: FormRecord;
    partnerGroup?: TaxResponseGroup;
    externalActivations?: Record<number, boolean>;
    override nestedUtils?: Record<number, FormUtilAccountant[]> = undefined;

    constructor(
        id: string,
        question: NestedQuestion,
        state: QuestionStateService,
        canEdit?: boolean,
        group?: TaxResponseGroup,
        declarantGroup?: TaxResponseGroup,
        partnerGroup?: TaxResponseGroup,
        forceAccountantForm?: boolean,
    ) {
        question.inputFields?.filter((f) => f.required && !f.syncableToSilverfin).forEach((f) => (f.required = false));
        super(id, question, state, canEdit, group, group, declarantGroup, partnerGroup);
        this.setup(declarantGroup, partnerGroup);
        this.setupDefaultValues();
        if (this.form === undefined && forceAccountantForm === true) {
            this.initForm();
        }
        this.declarantGroup = declarantGroup;
        this.partnerGroup = partnerGroup;
        [declarantGroup, partnerGroup]
            .filter((group) => group !== undefined)
            .forEach((group) => this.stateService.resolveExternalGroupId(this.question, this.id, group!.id));
    }

    override setup(declarantGroup?: TaxResponseGroup, partnerGroup?: TaxResponseGroup) {
        this.form = this.originalData !== undefined || !this.question.multi ? this.generateForm(this.originalData) : undefined;
        this.resetUuidMap();
        this.declarantForm = declarantGroup !== undefined ? this.generateForm(declarantGroup, true) : undefined;
        this.declarantForm?.disable();
        this.partnerForm = partnerGroup !== undefined ? this.generateForm(partnerGroup, true) : undefined;
        this.partnerForm?.disable();
        this.bufferedFiles = [];

        let nestedUtils = undefined as Record<number, FormUtilAccountant[]> | undefined;
        this.question.inputFields
            ?.filter((field) => field.type == QuestionType.NestedQuestion)
            .forEach((field) => {
                nestedUtils = nestedUtils ?? {};
                nestedUtils[field.id] = [];
                const matchedData = matchGroups(
                    field.nestedQuestion!,
                    ...[this.originalData, declarantGroup, partnerGroup].map(
                        (group) => group?.records.find((rec) => rec.inputField === field.id)?.nestedGroups,
                    ),
                );
                field.required = field.required && field.syncableToSilverfin;
                matchedData.forEach((dataMatch) => {
                    nestedUtils![field.id].push(
                        new FormUtilAccountant(
                            uuidv4(),
                            field.nestedQuestion!,
                            this.stateService,
                            this.editable,
                            dataMatch.accountant,
                            dataMatch.declarant,
                            dataMatch.partner,
                        ),
                    );
                });
            });
        this.nestedUtils = nestedUtils;
        if (this.form !== undefined) {
            this.fieldActivated = generateActivations(this.question.inputFields ?? [], [this.form], false, this.stateService);
            this.enableDisableByActivations();
        }
        const externalForms = [this.declarantForm, this.partnerForm].filter((form) => form !== undefined) as FormRecord[];
        if (externalForms.length > 0) {
            this.externalActivations = generateActivations(this.question.inputFields ?? [], externalForms, true, this.stateService);
        }
        this.setActivationSubscriptions();
        this.markAsPristine();
    }

    initForm(data?: TaxResponseGroup) {
        this.cleanUpSelfActivations();
        this.clearGroupFromState();
        this.form = this.generateForm(data);
        this.fieldActivated = generateActivations(this.question.inputFields ?? [], [this.form], false, this.stateService);
        this.enableDisableByActivations();
        this.setActivationSubscriptions();
    }

    private enableDisableByActivations() {
        Object.keys(this.fieldActivated ?? {}).forEach((key) => {
            const keyField = this.question.inputFields?.find((field) => field.id.toString() === key);
            if (keyField !== undefined && this.form?.controls[keyField.tag] !== undefined) {
                if (this.editable && this.fieldActivated[Number(key)] !== false && !keyField.readOnly) {
                    this.form?.controls[keyField.tag].enable();
                } else {
                    this.form?.controls[keyField.tag].disable();
                }
            }
        });
    }

    clearForm() {
        this.cleanUpActivations();
        this.stateService.clearQuestionGroupState(this.question.id, this.id);
        this.form = undefined;
        this.bufferedFiles = [];
        this.fieldActivated = {};
        //Remove or clean up all nested utils
        Object.keys(this.nestedUtils ?? {}).forEach((key) => {
            this.nestedUtils![Number(key)].forEach((util) => this.removeNestedUtil(Number(key), util.id));
        });
    }

    override resetField(field: QuestionField) {
        if (this.form === undefined) {
            return;
        }
        this.form.get(field.tag)?.patchValue([QuestionType.Check, QuestionType.Radio].includes(field.type) ? false : null);
        this.form.get(field.tag)?.removeValidators(Validators.required);
        this.form.get(field.tag)?.updateValueAndValidity();
        this.form.get(field.tag)?.markAsPristine();
        // Clear nested field when resetting
        if (field.type == QuestionType.NestedQuestion && Object.keys(this.nestedUtils ?? {}).some((key) => key === field.id.toString())) {
            this.nestedUtils![field.id].forEach((util) => this.removeNestedUtil(field.id, util.id));
        }
    }

    override isDirty(): boolean {
        return (
            this.untrackedChanges.length > 0 ||
            (this.form?.dirty ?? false) ||
            (this.question.multi && (this.originalData === undefined) !== (this.form === undefined)) ||
            this.getNestedUtils<FormUtilAccountant>().some((util) => util.isDirty())
        );
    }

    copyData(role: PersonType.Owner | PersonType.Partner, tag?: string, fileUuid?: string) {
        const group = role === PersonType.Owner ? this.declarantGroup : this.partnerGroup;
        const form = role === PersonType.Owner ? this.declarantForm : this.partnerForm;
        if (group === undefined || form === undefined) {
            console.debug('cannot copy data for non-existing data for role', role);
            return;
        }
        if (this.form === undefined) {
            this.initForm();
        }
        if (tag === undefined) {
            this.question.inputFields
                ?.filter((field) => form.controls[field.tag] !== undefined)
                .forEach((field) => (!field.readOnlyOrNonSyncable ? this.copyData(role, field.tag, fileUuid) : undefined));
            this.copyNestedData(role);
            this.fieldActivated = generateActivations(this.question.inputFields ?? [], [this.form!], false, this.stateService);
            this.enableDisableByActivations();
            return;
        }
        const field = this.question.inputFields?.find((field) => field.tag === tag);
        if (field === undefined) {
            console.debug('cannot copy: could not find question field with tag', tag);
            return;
        }
        if ([QuestionType.File, QuestionType.SingleFile].includes(field.type)) {
            const newValues = fileUuid !== undefined ? [fileUuid] : form.controls[field.tag]?.value ?? [];
            const updatedValues =
                field.type === QuestionType.SingleFile
                    ? new Set([...newValues])
                    : new Set([...(this.form!.controls[field.tag].value ?? []), ...newValues]);
            this.form!.controls[field.tag].patchValue([...updatedValues]);
            this.copyFiles(group, field, newValues);
            this.resetUuidMap(this.getFileUuids());
            return;
        }
        let newValue = form.controls[field.tag].value;
        if (field.type === QuestionType.SelectDependent) {
            // Get the group uid via the external state
            const groupUid = this.stateService
                .getCurrentFieldStateExternal(field.selectDependentDisplayField!)
                .find((state) => state.groupId === newValue)?.groupUid;
            const groupId = this.stateService
                .getCurrentFieldState(field.selectDependentDisplayField!)
                .find((state) => state.groupUid === groupUid)?.groupId;
            newValue = groupId ?? null;
        }
        this.form!.controls[field.tag].markAsDirty();
        this.form!.controls[field.tag].patchValue(newValue);
    }

    private copyNestedData(role: PersonType.Owner | PersonType.Partner) {
        //Makes the nested data an exact copy of the data of the person e.g. removing added data as well
        if (this.nestedUtils !== undefined) {
            Object.keys(this.nestedUtils).forEach((key) => {
                this.nestedUtils![Number(key)] = this.nestedUtils![Number(key)].filter(
                    (util) => util.declarantForm !== undefined || util.partnerForm !== undefined,
                );
            });
            this.getNestedUtils<FormUtilAccountant>().forEach((util) => {
                util.copyData(role);
            });
        }
    }

    private copyFiles(group: TaxResponseGroup, field: QuestionField, uuids?: string[]) {
        const docs = group.records.find((rec) => rec.inputField == field.id)?.documents ?? [];
        if (field.type === QuestionType.SingleFile) {
            this.bufferedFiles = this.bufferedFiles.filter((fUtil) => fUtil.fieldId !== field.id);
        }
        const currentFiles = this.bufferedFiles.map((fUtil) => fUtil.uuid);
        for (const document of docs.filter(
            (doc) => !currentFiles.includes(doc.uuid) && (uuids === undefined || uuids?.includes(doc.uuid)),
        )) {
            this.copyDocumentToBufferedFiles(document, field);
        }
    }

    override removeNestedUtil(idx: number, utilId: string): void {
        let util = (this.nestedUtils?.[idx] ?? []).find((util) => util.id === utilId);
        if (util === undefined) {
            console.debug('util to remove not found:', idx, utilId);
            return;
        }
        if (util.declarantForm === undefined && util.partnerForm === undefined) {
            super.removeNestedUtil(idx, utilId);
            return;
        }
        if (util.form == undefined) {
            return;
        }
        util.clearForm();
        if (this.untrackedChanges.includes(utilId)) {
            this.untrackedChanges = this.untrackedChanges.filter((ch) => ch !== utilId);
        } else {
            this.untrackedChanges.push(utilId);
        }
    }

    override addNestedUtil(idx: number) {
        const field = this.question.inputFields?.find((field) => field.id == idx);
        if (field === undefined) {
            return;
        }
        const uid = uuidv4();
        this.nestedUtils?.[idx].push(
            new FormUtilAccountant(uid, field.nestedQuestion!, this.stateService, this.editable, undefined, undefined, undefined, true),
        );
        this.untrackedChanges.push(uid);
    }

    hasDifferences(): boolean {
        let result = this.getNestedUtils<FormUtilAccountant>().some((util) => util.hasDifferences());
        const externals = [this.declarantForm, this.partnerForm].filter((form) => form !== undefined);
        const eqPipe = new FieldEqualsPipe(this.stateService);

        if (!result && externals.length > 0) {
            result =
                this.question.inputFields?.some((field) => {
                    return externals.some(
                        (extForm) => !eqPipe.transform(field, this.form?.controls[field.tag]?.value, extForm!.controls[field.tag]?.value),
                    );
                }) ?? false;
        }
        return result;
    }

    reset() {
        this.setup(this.declarantGroup, this.partnerGroup);
    }
}
